import React, { useMemo, useState } from 'react'
import { CircleHelp, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styles from './SuggestedPrompts.module.scss'
import { getDesktopSuggestions } from '../DashboardHeader/dashboardActionsConfig.tsx'
import { ANALIZAR_DOC_OBRA_COTIZACIONES_PROMPT } from '../../../utils/conversationalMode/conversationalQuickActions.ts'
import ModalOverlay from '../../UI/ModalOverlay/ModalOverlay.tsx'
import useIsApple from '../../../hooks/useIsApple.ts'
import FeedPreviewPanel from './FeedPreviewPanel/FeedPreviewPanel.tsx'

interface SuggestedPromptsProps {
  userId?: string
  userData?: any
}

type SuggestionTab = 'publicar' | 'explorar' | 'novedades' | 'mis-contenidos' | 'herramientas'

const TAB_ORDER: SuggestionTab[] = ['novedades', 'publicar', 'explorar', 'mis-contenidos', 'herramientas']

const TAB_LABELS: Record<SuggestionTab, string> = {
  'publicar': 'Publicar',
  'explorar': 'Explorar',
  'novedades': 'Novedades',
  'mis-contenidos': 'Mis contenidos',
  'herramientas': 'Herramientas'
}

const ANALIZAR_DOCS_SUGGESTION_ID = 'analizar-docs-obra-cotizaciones'

const QUESTION_BY_ID: Record<string, string> = {
  [ANALIZAR_DOCS_SUGGESTION_ID]: ANALIZAR_DOC_OBRA_COTIZACIONES_PROMPT,
  'cotizar-obras': 'Quiero cotizar en obras privadas disponibles, ¿por dónde empiezo?',
  'obras-publicas': 'Estoy buscando licitaciones u obras públicas, ¿dónde las encuentro?',
  'mis-proyectos': 'Quiero gestionar mis proyectos publicados, ¿cómo accedo?',
  'estimacion-costos': 'Necesito una estimación de costos, ¿cómo la calculo?',
  'cotizar-materiales': 'Quiero pedir una cotización de materiales, ¿me ayudás a armarla?',
  'cotizaciones-materiales': 'Busco cotizaciones disponibles de materiales, ¿dónde las veo?',
  'mis-cotizaciones-materiales': 'Quiero ver mis cotizaciones de materiales, ¿cómo ingreso?',
  'mi-comercio': 'Quiero administrar mi comercio en Edify, ¿cómo accedo?',
  'mis-posteos-muro': 'Quiero ver mis posteos en el muro de mi perfil, ¿cómo accedo?',
  'encontrar-profesionales': 'Estoy buscando una empresa de instalaciones sanitarias, ¿dónde puedo encontrarla?',
  'postularme-ofertas': 'Quiero postularme a ofertas de trabajo, ¿dónde las veo?',
  'publicar-oferta-trabajo': 'Necesito publicar una vacante, ¿cómo lo hago?',
  'mis-postulaciones': 'Quiero ver mis postulaciones, ¿cómo accedo?',
  'explorar-servicios': 'Estoy buscando servicios y productos, ¿dónde los exploro?',
  'ofrecer-servicios': 'Quiero publicar un posteo o producto/servicio, ¿cómo lo hago?',
  'mis-productos-servicios': 'Necesito gestionar mis productos y servicios, ¿dónde entro?'
}

const SUGGESTION_TABS: Record<string, SuggestionTab> = {
  [ANALIZAR_DOCS_SUGGESTION_ID]: 'publicar',
  'cotizar-materiales': 'publicar',
  'publicar-oferta-trabajo': 'publicar',
  'ofrecer-servicios': 'publicar',
  'cotizar-obras': 'explorar',
  'obras-publicas': 'explorar',
  'cotizaciones-materiales': 'explorar',
  'encontrar-profesionales': 'explorar',
  'explorar-servicios': 'explorar',
  'postularme-ofertas': 'explorar',
  'mis-proyectos': 'mis-contenidos',
  'mi-comercio': 'mis-contenidos',
  'mis-posteos-muro': 'mis-contenidos',
  'mis-cotizaciones-materiales': 'mis-contenidos',
  'mis-postulaciones': 'mis-contenidos',
  'mis-productos-servicios': 'mis-contenidos',
  'estimacion-costos': 'herramientas'
}

const SuggestedPrompts: React.FC<SuggestedPromptsProps> = ({ userId, userData }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isApple = useIsApple()
  const [activeTab, setActiveTab] = useState<SuggestionTab>('novedades')
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  const suggestions = useMemo(
    () => getDesktopSuggestions(navigate, userId, userData, dispatch),
    [navigate, userId, userData, dispatch]
  )
  const availableTabs = useMemo(() => {
    const tabs = new Set<SuggestionTab>(['novedades'])
    suggestions.forEach((suggestion) => {
      const tab = SUGGESTION_TABS[suggestion.id]
      if (tab) {
        tabs.add(tab)
      }
    })
    return TAB_ORDER.filter((tab) => tabs.has(tab))
  }, [suggestions])
  const resolvedTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0]
  const isNovedadesTab = resolvedTab === 'novedades'
  const filteredSuggestions = useMemo(() => {
    if (!resolvedTab) {
      return suggestions
    }
    return suggestions.filter((suggestion) => SUGGESTION_TABS[suggestion.id] === resolvedTab)
  }, [suggestions, resolvedTab])

  return (
    <div className={styles.container}>
      <div className={styles.tabsRow}>
        <div className={styles.tabs}>
          {availableTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              data-tutorial={`tab-${tab}`}
              className={`${styles.tabButton} ${tab === resolvedTab ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={styles.helpButton}
          aria-label="Ayuda"
          onClick={() => setIsHelpOpen(true)}
        >
          <CircleHelp size={16} />
        </button>
      </div>
      <div className={styles.tabPanel}>
        {isNovedadesTab ? (
          <FeedPreviewPanel active={isNovedadesTab} />
        ) : (
          <div className={styles.grid}>
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                type="button"
                className={`${styles.card} ${isApple ? styles.cardApple : ''}`}
                onClick={suggestion.action}
              >
                <span className={styles.cardInner}>
                  {suggestion.id === ANALIZAR_DOCS_SUGGESTION_ID ? (
                    <span className={`${styles.badge} ${styles.badgeNew}`}>Nuevo</span>
                  ) : (
                    <span className={styles.badge}>{String(index + 1).padStart(2, '0')}</span>
                  )}
                  <span className={styles.label}>
                    {QUESTION_BY_ID[suggestion.id] || suggestion.label}
                  </span>
                  <span className={styles.trailingIcon}>
                    <Plus size={16} />
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <ModalOverlay isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)}>
        <div className={styles.helpModal}>
          <h3 className={styles.helpTitle}>Prompts sugeridos</h3>
          <p className={styles.helpText}>
            Estas preguntas son ejemplos para ayudarte a empezar. Podés elegir una o escribirle
            al agente lo que necesites.
          </p>
          <button
            type="button"
            className={styles.helpCta}
            onClick={() => setIsHelpOpen(false)}
          >
            Entendido
          </button>
        </div>
      </ModalOverlay>
    </div>
  )
}

export default SuggestedPrompts
